/**
 * The row API on the wire (PLAN §6.5): listing and fetching rows, and posting
 * patch batches. Identical on the cloud and the node (§10.1).
 *
 * The row and patch shapes themselves are the domain's (`@norna/domain/row.js`)
 * — the schema a handler validates with is the schema `applyPatch` types
 * against, so there is one definition. This module adds only what is about
 * transport: paging, the catch-up cursor, batch limits, per-patch results.
 *
 * `.strict()` everywhere: an unknown key is a client bug or a version skew,
 * and silently dropping it would lose data without a trace.
 */
import { patchRejectionWireSchema } from './rowDocument.js';
import { rowPatchWireSchema, rowWireSchema } from './rowDocument.js';
import { minUlidAt, ulidTimeMs } from '@norna/domain/ulid.js';
import { opaqueIdSchema, ulidSchema } from '@norna/domain/primitives.js';
import { z } from 'zod';

/**
 * Most patches in one batch, REST or WS. API Gateway WebSocket frames are
 * capped at 128 KB (PLAN §6.5) and a batch is one frame, so the bound is set
 * here rather than discovered as a dropped connection.
 */
export const MAX_PATCHES_PER_BATCH = 500;

/** Rows per page when `limit` is not given, and the most a page may hold. */
export const DEFAULT_PAGE_SIZE = 500;
const MAX_PAGE_SIZE = 1000;

/**
 * How far BEFORE the cursor a `changedSince` query starts (PLAN §6.5: "query
 * from `cursor − 5 s`, because the GSI is eventually consistent"). A row
 * written just before the cursor was minted may not have been visible to the
 * query that minted it; re-reading the last 5 s catches it. The price is that
 * rows repeat across catch-ups — harmless, because merging a row twice is a
 * no-op (`mergeRows` is idempotent).
 */
export const CHANGED_SINCE_OVERLAP_MS = 5_000;

/**
 * The inclusive lower bound on `changedAt` a server queries for
 * `changedSince=<cursor>`: the smallest ULID `CHANGED_SINCE_OVERLAP_MS` before
 * the cursor's time. One definition, so the cloud (DynamoDB GSI range) and
 * the node (SQLite index range) overlap by exactly the same amount.
 */
export function changedSinceLowerBound(cursor: string): string {
  return minUlidAt(Math.max(0, ulidTimeMs(cursor) - CHANGED_SINCE_OVERLAP_MS));
}

/**
 * A catch-up cursor: OPAQUE, and interpreted only by the store that issued it.
 *
 * The two hosts mint different things (PLAN §10.1): the cloud's is the
 * greatest `changedAt` ULID of the partition, queried on the GSI with the
 * `changedSinceLowerBound` overlap; the node's is its SQLite store id plus a
 * monotonically increasing local change sequence, which a wall clock stepping
 * backwards across a restart cannot reorder. One contract type covers both, so
 * a client never parses a cursor — it stores the last one and hands it back.
 *
 * **A cursor a store did not issue is answered with a full replay**, never an
 * error: an SPA that switches its primary backend (node A → node B → cloud,
 * PLAN §10.2) sends the old backend's cursor to the new one, and every row is
 * the correct (if larger) answer. Only a string that is not a cursor at all is
 * a `bad-request`.
 */
export const cursorWireSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/, { message: 'not a cursor' });

/** An opaque continuation token; only the server that issued it interprets it. */
export const pageTokenWireSchema = z.string().min(1).max(2048);

/** A page size query parameter (a string on the wire, coerced). */
export const pageLimitWireSchema = z.coerce.number().int().min(1).max(MAX_PAGE_SIZE);

/**
 * Query and path parameters carry `.describe()` text: it is what the generated
 * OpenAPI shows for each parameter (`openapi.ts`).
 */
export const PAGE_TOKEN_DESCRIPTION = 'Continue a listing: the previous page’s `nextPageToken`. The other parameters must repeat unchanged.';
export const PAGE_LIMIT_DESCRIPTION = 'Page size, 1–1000; default 500.';
const rundownIdParam = ulidSchema.describe('The rundown’s id (a ULID).');

export const rundownPathWireSchema = z.object({ rundownId: rundownIdParam }).strict();
export const rowPathWireSchema = z.object({ rundownId: rundownIdParam, rowId: ulidSchema.describe('The row’s id (a ULID).') }).strict();

/**
 * `GET /rundowns/{rundownId}/rows`. Every row is returned, soft-deleted ones
 * included: this is the replication surface, and a deletion is a row state a
 * replica must learn.
 */
export const listRowsQueryWireSchema = z
  .object({
    pageToken: pageTokenWireSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitWireSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
    parentId: ulidSchema.optional().describe('Only the direct children of this row (parallel fetch per segment, PLAN §6.5).'),
    // Opaque (`cursorSchema`): the cloud answers every row with changedAt >=
    // changedSinceLowerBound(cursor); the node, every row after its sequence.
    changedSince: cursorWireSchema
      .optional()
      .describe(
        'Catch-up: only rows changed since this cursor (a previous response’s `cursor`; opaque — store it, never parse it). ' +
          'The cloud re-reads the 5 s (CHANGED_SINCE_OVERLAP_MS) before the cursor, because its index is eventually consistent, so rows may repeat: ' +
          'merge them, never append them. A cursor another backend issued is answered with every row.',
      ),
  })
  .strict();
export type ListRowsQueryWire = z.infer<typeof listRowsQueryWireSchema>;

export const listRowsResponseWireSchema = z
  .object({
    rows: z.array(rowWireSchema),
    /** Null on the last page. */
    nextPageToken: pageTokenWireSchema.nullable(),
    /**
     * The high-water mark to pass as `changedSince` next time: the rundown's
     * latest change when the listing STARTED (not merely the greatest on this
     * page, and the same on every page of one listing), so a catch-up from it
     * misses nothing written before or during the listing. Opaque.
     */
    cursor: cursorWireSchema,
  })
  .strict();

export const getRowResponseWireSchema = z.object({ row: rowWireSchema }).strict();

/** `POST /rundowns/{rundownId}/patches` — for clients without a socket and for the MOS gateway (§6.5). */
export const patchBatchRequestWireSchema = z
  .object({ patches: z.array(rowPatchWireSchema).min(1).max(MAX_PATCHES_PER_BATCH) })
  .strict();

/**
 * The outcome of one patch. Results come back in request order, one per patch.
 *
 * - `ack`: merged. `applied` lists the fields that changed (empty when the
 *   patch was stale everywhere — still an ack, the LWW simply kept newer
 *   values), and `row` is the canonical state after the merge (§6.2
 *   "Server echoes carry the canonical HLC").
 * - `nack`: refused, with every reason. `row` is the canonical current state
 *   (null when the row does not exist), so the client rolls its optimistic
 *   change back to it.
 */
export const patchResultWireSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('ack'),
      clientOpId: opaqueIdSchema,
      rowId: ulidSchema,
      applied: z.array(z.string()),
      row: rowWireSchema,
    })
    .strict(),
  z
    .object({
      status: z.literal('nack'),
      clientOpId: opaqueIdSchema,
      rowId: ulidSchema,
      rejected: z.array(patchRejectionWireSchema).min(1),
      row: rowWireSchema.nullable(),
    })
    .strict(),
]);
export type PatchResultWire = z.infer<typeof patchResultWireSchema>;

export const patchBatchResponseWireSchema = z.object({ results: z.array(patchResultWireSchema) }).strict();
export type PatchBatchResponseWire = z.infer<typeof patchBatchResponseWireSchema>;
